import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Download, RefreshCw, Database, HardDrive, Clock, Calendar, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

import { BACKEND_URL } from '../../config/api';
const BackupManagement = () => {
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupHistory, setBackupHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [downloading, setDownloading] = useState(null); // Track which backup is downloading
  const [message, setMessage] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('lms_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchBackupStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backups/status`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setBackupStatus(data);
    } catch (error) {
      console.error('Failed to fetch backup status:', error);
    }
  };

  const fetchBackupHistory = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backups/history`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setBackupHistory(data.backups || []);
    } catch (error) {
      console.error('Failed to fetch backup history:', error);
    }
  };

  const triggerBackup = async () => {
    setTriggering(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backups/trigger`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Backup started! This will take 30-60 seconds...' });
        
        // Refresh after 35 seconds
        setTimeout(() => {
          fetchBackupStatus();
          fetchBackupHistory();
        }, 35000);
      } else {
        setMessage({ type: 'error', text: 'Failed to start backup' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error triggering backup: ' + error.message });
    } finally {
      setTriggering(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    await Promise.all([fetchBackupStatus(), fetchBackupHistory()]);
    setLoading(false);
  };

  const downloadBackup = async (backupDate) => {
    setDownloading(backupDate); // Set downloading state
    try {
      setMessage({ type: 'success', text: `Starting download for backup ${backupDate}...` });
      
      const response = await fetch(
        `${BACKEND_URL}/api/admin/backups/download/${backupDate}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('lms_token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lms_backup_${backupDate}.tar.gz`);
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      setMessage({ type: 'success', text: `Backup ${backupDate} downloaded successfully!` });
    } catch (error) {
      console.error('Download error:', error);
      setMessage({ type: 'error', text: `Failed to download backup: ${error.message}` });
    } finally {
      setDownloading(null); // Clear downloading state
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatSize = (mb) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Backup Management</h1>
          <p className="text-gray-500 mt-1">Automated daily backups at 11:59 PM IST</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={refreshData}
            variant="outline"
            disabled={loading}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={triggerBackup}
            disabled={triggering || !backupStatus?.enabled}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {triggering ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating Backup...
              </>
            ) : (
              <>
                <Database className="h-4 w-4 mr-2" />
                Create Backup Now
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <Alert className={message.type === 'success' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}>
          <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Backup Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {backupStatus?.enabled ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              System Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Automated Backups:</span>
                <span className="font-semibold">
                  {backupStatus?.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Storage Type:</span>
                <span className="font-semibold capitalize">{backupStatus?.storage_type || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Retention:</span>
                <span className="font-semibold">{backupStatus?.retention_days || 30} days</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Backup */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-500" />
              Next Scheduled Backup
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Schedule:</span>
                <span className="font-semibold">{backupStatus?.schedule || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Next Run:</span>
                <span className="font-semibold">
                  {backupStatus?.next_run ? formatDate(backupStatus.next_run) : 'N/A'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Storage Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HardDrive className="h-5 w-5 text-purple-500" />
              Storage Location
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Total Backups:</span>
                <span className="font-semibold">{backupHistory.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Path:</span>
                <span className="font-semibold text-xs truncate" title={backupStatus?.backup_path}>
                  {backupStatus?.backup_path?.split('/').pop() || 'backups'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Backup History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Backup History
          </CardTitle>
          <CardDescription>
            View and manage your backup history. Backups are automatically deleted after {backupStatus?.retention_days || 30} days.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {backupHistory.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No backups found</p>
              <p className="text-sm mt-2">Click "Create Backup Now" to create your first backup</p>
            </div>
          ) : (
            <div className="space-y-3">
              {backupHistory.map((backup, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Database className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-semibold">{backup.date}</p>
                      <p className="text-sm text-gray-500">
                        {backup.timestamp ? formatDate(backup.timestamp) : 'Unknown time'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold">{formatSize(backup.size_mb)}</p>
                      <p className="text-sm text-gray-500">
                        {Object.keys(backup.files || {}).length} files
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadBackup(backup.date)}
                      disabled={downloading === backup.date}
                    >
                      {downloading === backup.date ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Information Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-900">📋 Backup Information</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-blue-800 space-y-2">
          <p><strong>What's included:</strong></p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Complete MongoDB database (~0.5 MB)</li>
            <li>All user uploads (images, recordings, files) (~38 MB)</li>
            <li>Environment configuration files (.env)</li>
          </ul>
          <p className="mt-4"><strong>Automated Schedule:</strong> Backups run daily at 11:59 PM IST</p>
          <p><strong>Storage Location:</strong> {backupStatus?.backup_path || '/app/backend/backups'}</p>
          <p><strong>Retention Policy:</strong> Backups older than {backupStatus?.retention_days || 30} days are automatically deleted</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default BackupManagement;
